/**
 * ui — style helpers (and the one selection hook) shared by the native kit.
 *
 * Non-component exports only (keeps `react-refresh/only-export-components`
 * happy and gives the kit one place to change a shape). Everything reads L0
 * tokens through `var(--…)`; no kit file hard-codes a hex, a px font-size or a
 * shadow — same rule the design kit puts on widget authors.
 */
import { useCallback, useState } from 'react';
import { useViewIR } from '../viewIRContext';
import { TONE_VAR, TONE_SOFT_VAR } from '../tokens';
import { formatNumber, getPath, hasInterp } from '../paths';
import { familyOf, skeletonOf } from '../families';

/**
 * THE COVER LADDER — where a row's picture lives, in order of preference.
 *
 * These five are the shapes the feed actually ships, and they are the DEFAULT,
 * not the law: `props.sources` replaces them with the blueprint's own list
 * (`["cover", "images.0", "photo_url"]`, dotted paths included). Every widget
 * used to carry a `firstPhoto`-shaped derive whose entire job was to run this
 * ladder before handing the result to a component that then ran its own — the
 * ladder belongs to the component that needs a picture.
 *
 * It lives HERE, beside the other non-component helpers, because more than one
 * media surface needs it: the stage (`Carousel`), the well grid (`MediaPicker`)
 * and the edit page (`ScanEditor`) all answer "where is this row's picture" and
 * a second spelling of that answer is how two of them end up disagreeing about
 * which field wins.
 */
export const DEFAULT_SOURCES = ['image_url', 'photo_url', 'src', 'url', 'images.0'];

/** The first non-empty of `sources` on a row (a bare string IS its own src). */
export function slideSource(s, sources) {
  if (typeof s === 'string') return s;
  if (!s || typeof s !== 'object') return '';
  for (const key of sources) {
    const v = getPath(s, key);
    if (typeof v === 'string' && v) return v;
    if (Array.isArray(v) && typeof v[0] === 'string' && v[0]) return v[0];
  }
  return '';
}

/** `props.sources` → a ladder, or the default one. */
export function sourceLadder(sources) {
  const list = (Array.isArray(sources) ? sources : []).filter((s) => typeof s === 'string' && s);
  return list.length ? list : DEFAULT_SOURCES;
}

/**
 * Coerce the many shapes a slide list arrives in to `{ src, label, fit }`.
 *
 * `labelField` names the per-slide caption a stage prints over the picture
 * (prod's `.slabel`) — "Original", "Retouched", a version name. Without one the
 * row's `name` still answers, which is what it always did.
 */
export function toSlides(raw, { sources = DEFAULT_SOURCES, labelField = null } = {}) {
  const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
  const out = [];
  const seen = new Set();
  for (const s of list) {
    const src = slideSource(s, sources);
    if (!src || seen.has(src)) continue; // SEALED: dedup the original once
    seen.add(src);
    const label = typeof s === 'object' && s
      ? String((labelField ? (getPath(s, labelField) ?? s.name) : s.name) ?? '')
      : '';
    out.push({ src, label, fit: out.length === 0 ? 'cover' : 'contain' });
  }
  return out;
}

export const TYPE_STYLE = {
  display: { fontSize: 'var(--t-display)', fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.05 },
  title: { fontSize: 'var(--t-2xl)', fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15 },
  subtitle: { fontSize: 'var(--t-lg)', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.25 },
  body: { fontSize: 'var(--t-md)', fontWeight: 400, lineHeight: 1.45 },
  caption: { fontSize: 'var(--t-sm)', fontWeight: 400, lineHeight: 1.35 },
  // app 0.10.2+: a compact row's one-line name, and its 12px status word
  label: { fontSize: 14, fontWeight: 600, lineHeight: 1.3 },
  small: { fontSize: 12, fontWeight: 500, lineHeight: 1.35 },
  eyebrow: { fontSize: 'var(--t-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', lineHeight: 1.2 },
};

/**
 * "THE WIDGET GENUINELY HOLDS NOTHING" — the one reading of the feed that
 * gates every first-run state, and the reason it lives here rather than inside
 * `EmptyState`: TWO layers have to agree on it. The empty state uses it to
 * decide whether to draw at all, and a collection uses it to stand down when
 * the page already owns that reading (see `pageOwnsFirstRun`). Two spellings
 * of "empty" would let the page show both empties, or neither.
 *
 * An in-flight capture is NOT nothing: the row is on its way, the collection is
 * already drawing a placeholder for it, and a first-run screen over that
 * placeholder tells the user to start something they just started.
 *
 * IT COUNTS ITEMS, NOT FEED ROWS (`__itemCount`, built in `buildRenderContext`).
 * `entries.length` answers "did the feed carry any bytes", and every fold that
 * drops rows makes that a different question: plant-expert's live instance is
 * two `care_log` rows whose plant never landed — an orphan bucket the fold
 * drops — so the feed was non-empty, `garden` was empty, the blueprint's
 * `emptyGarden` fired correctly, and this gate suppressed it. That page drew
 * NOTHING. What the gate is FOR survives the change, because items are items
 * whichever selector they land in: a wardrobe holding garments but no outfits
 * still counts its garments, so first-run stays shut on the outfits tab.
 *
 * The `??` ladder is for every ctx built OUTSIDE `buildRenderContext` — tests,
 * component previews, host mocks — which carry no `__itemCount` and must keep
 * reading exactly as they did.
 */
export function widgetHoldsNothing(ctx) {
  return (ctx?.__itemCount ?? ctx?.entries?.length ?? 0) === 0
    && (ctx?.pending?.adds?.length || 0) === 0;
}

export function toneColor(tone) {
  return TONE_VAR[tone] || (tone ? String(tone) : 'var(--text)');
}

export function toneSoft(tone) {
  return TONE_SOFT_VAR[tone] || 'var(--surface-3)';
}

const GAP = {
  1: 'var(--sp-1)', 2: 'var(--sp-2)', 3: 'var(--sp-3)',
  4: 'var(--sp-4)', 5: 'var(--sp-5)', 6: 'var(--sp-6)',
};

/**
 * `gap: "3"` | `gap: 12` | `gap: "--sp-3"` | `gap: "var(--sp-3)"` → a CSS length.
 *
 * The BARE TOKEN NAME is accepted because that is what authors actually write:
 * three shipped blueprints declare `"gap": "--sp-3"`, which fell through as the
 * literal `--sp-3` — not a CSS length, so every one of those grids silently
 * lost its gap. A token id and its variable name mean the same thing here.
 */
export function gapValue(g, fallback = 'var(--sp-3)') {
  if (g == null) return fallback;
  if (typeof g === 'number') return `${g}px`;
  const s = String(g);
  if (GAP[s]) return GAP[s];
  if (/^(sp-[1-6]|r|r-(sm|lg|xl|pill))$/.test(s)) return `var(--${s})`;
  return s.startsWith('--') ? `var(${s})` : s;
}

/**
 * OVER-PICTURE INK — the one place the kit states a colour rather than naming
 * a token, and the reason is the picture.
 *
 * Every other surface in the kit is a token because the THEME decides how it
 * reads. Ink laid over a photograph is not on a theme surface: it is on the
 * photograph, which is as dark or as light in dark mode as it was in light.
 * `var(--text)` over a poster is black text on a black night sky in light mode
 * and white on a snowfield in dark — the token is exactly the wrong answer.
 * So the poster face carries its own fixed pair (a near-white ink, and the
 * scrim that guarantees the contrast under it), identical in both themes and
 * mirrored value-for-value by the Flutter twin (`tokens.dart`).
 *
 * The values are the prod WebView's `--media-ink` / `--media-overlay-*`.
 */
export const MEDIA_INK = '#F7F7F4';
export const MEDIA_INK_2 = 'rgba(247,247,244,0.72)';
export const MEDIA_OVERLAY_STRONG = 'rgba(12,12,13,0.90)';
export const MEDIA_OVERLAY_MID = 'rgba(12,12,13,0.62)';

/**
 * The bottom SCRIM: a gradient that darkens the foot of a picture so overlaid
 * ink keeps its contrast whatever the photograph does. It reaches 42% up the
 * frame and is fully transparent at the top, so the picture is not tinted —
 * only the strip the words sit on.
 */
export const MEDIA_SCRIM_HEIGHT = '42%';
export const MEDIA_SCRIM = `linear-gradient(to top, ${MEDIA_OVERLAY_STRONG} 0%, ${MEDIA_OVERLAY_MID} 46%, transparent 100%)`;

/** `overlayAlign` → the flex/text alignment pair the poster overlay uses. */
export const OVERLAY_ALIGN = {
  start: { items: 'flex-start', text: 'left' },
  center: { items: 'center', text: 'center' },
  end: { items: 'flex-end', text: 'right' },
};

/**
 * The FOUR CORNERS a badge may claim, as absolute offsets.
 *
 * Deliberately the same four names `Overlay`'s `OVERLAY_REGION` uses
 * (`top-start` · `top-end` · `bottom-start` · `bottom-end`): a corner is a
 * corner, and a second vocabulary for the same nine placements is how two parts
 * of one kit end up disagreeing about where "top right" is. A card can now pin
 * a badge to any of them WITHOUT an `Overlay` wrapper — which is what the
 * blueprint had to reach for, losing the card's own layout in the process.
 *
 * `top-end` is the historical hard-pin (`top:6,right:6`), so a `media.badge`
 * that names no region lands exactly where it always did.
 */
export const BADGE_CORNER = {
  'top-start': { top: 6, left: 6 },
  'top-end': { top: 6, right: 6 },
  'bottom-start': { bottom: 6, left: 6 },
  'bottom-end': { bottom: 6, right: 6 },
};

/**
 * The kit's ONE shimmer plate — a moving highlight over a `surface-3` track.
 *
 * It lives here because three surfaces need the identical wait: a picture that
 * has not decoded, a pending cell, and `EmptyState kind:"loading"`. Three
 * hand-rolled gradients would drift in speed and angle, and a page showing two
 * of them at once would read as two different kinds of waiting.
 */
export const SHIMMER_STYLE = {
  background: 'linear-gradient(100deg, var(--surface-3) 30%, var(--surface-2) 50%, var(--surface-3) 70%)',
  backgroundSize: '200% 100%',
  animation: 'viNativeShimmer 1.2s linear infinite',
};

/**
 * A component's family → the skeleton SHAPE that stands in for it while the
 * feed is in flight (`SkeletonPlate`, drawn by the interpreter for every
 * data-bound node).
 *
 * The family answers rather than a list of type names, so a component ADDED to
 * the registry gets a correct skeleton without this table being edited to know
 * about it — the same rule the dashboard's panels are gated by. Anything with
 * no opinion is text, which is the safest thing to be wrong about: two bars
 * take the space a line of copy would.
 */
const FAMILY_SKELETON = {
  collection: 'cards',
  dataviz: 'block',
  media: 'block',
  // A bound layout container is a visual AREA (my-wardrobe's flat-lay board),
  // not a line of text.
  layout: 'block',
  // …and so is a detail page. `chrome` is on this list because of one fact
  // about the fleet: every detail page's ROOT is an `ItemPage bind:"focusEntry"`
  // (ten of them across the nine blueprints), so the wait on a detail page is
  // the whole page. Two text bars would be a lie about what is coming; a plate
  // is the page's own silhouette. What the page must NOT do is render — with
  // `focusEntry` still null, `ItemPage` reaches either its `not-found` empty
  // ("This item is no longer here" — a verdict, about a row that is merely
  // late) or its developer placeholder.
  chrome: 'block',
  content: 'rows',
  status: 'rows',
};

const SKELETON_SHAPES = new Set(['cards', 'block', 'rows']);

/**
 * The registry may override the shape per component (`"skeleton"`), for the one
 * entry whose family does not predict its silhouette. Nothing declares it
 * today; it is the seam a hot-shipped component uses to disagree without a new
 * web bundle.
 */
export function skeletonShapeFor(type) {
  const declared = skeletonOf(type);
  if (SKELETON_SHAPES.has(declared)) return declared;
  return FAMILY_SKELETON[familyOf(type)] || 'rows';
}

/**
 * Is this AUTHORED line still waiting on the feed?
 *
 * The skeleton rule catches every node that BINDS. It cannot catch the other
 * half — authored copy with a read spliced into it: PageShell's eyebrow
 * `"{totals.calories_label} cal logged"` is a static string as far as `bind` is
 * concerned, so it renders, says "0 cal logged", and flips a second later. The
 * flip is the bug, not the zero.
 *
 * True only when the page is loading AND the author actually interpolated
 * something: a plain literal ("Today") is as true during the wait as after, and
 * shimmering it would hide a word the page already knows.
 */
export function waitsForData(ctx, authored) {
  return Boolean(ctx?.__loading) && hasInterp(authored);
}

/** A poster is a 2:3 movie poster unless the blueprint says otherwise. */
export const POSTER_ASPECT = '2 / 3';

/** A 44pt hit target is SEALED on every tappable kit element. */
export const hitTarget = { minHeight: 44, minWidth: 44 };

export const resetButton = {
  appearance: 'none',
  border: 'none',
  background: 'transparent',
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  textAlign: 'left',
};

/** Aspect ratio prop ("3/4", "1/1", 0.75) → a CSS aspect-ratio value. */
export function ratioValue(r, fallback = '1 / 1') {
  if (!r) return fallback;
  if (typeof r === 'number') return String(r);
  return String(r).replace('/', ' / ');
}

/**
 * The MONOGRAM pair — a deterministic hue and the initials for a key.
 *
 * FNV-1a is chosen because it is trivially portable: the Flutter twin can hash
 * the same string to the same number, so a pictureless row wears the same
 * colour on both runtimes. A per-runtime hash would quietly break the identity
 * contract in the one place a user would notice it.
 */
export const MONO_HUES = [8, 32, 48, 96, 152, 190, 216, 262, 292, 330];

export function hashKey(s) {
  let h = 0x811c9dc5;
  const str = String(s || '');
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** Up to two initials: word starts for latin, the first glyph otherwise. */
export function initialsOf(value) {
  const s = String(value ?? '').trim();
  if (!s) return '';
  if (!/[A-Za-z]/.test(s)) return [...s][0] || '';
  const words = s.split(/[\s·,/|-]+/).filter(Boolean);
  return words.slice(0, 2).map((w) => [...w][0]).join('').toUpperCase();
}

/**
 * "This reader asked for no motion." Read at render (never cached) so the
 * studio and the app answer the same way, and guarded because a test runtime
 * may not implement `matchMedia` at all — an unknown answer is NOT a reason to
 * suppress motion, so it degrades to false.
 */
export function prefersReducedMotion() {
  try {
    return Boolean(globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
  } catch {
    return false;
  }
}

/**
 * Long-press → THE HOST'S OPTIONS SHEET, never a delete on the gesture alone.
 * `onLongPress` fires after 500ms of touch-hold, and a right-click
 * (contextmenu) is the desktop equivalent — a long press on the Flutter kit IS
 * a right-click on the web kit, one gesture with one meaning on both.
 *
 * A collection cell emits `removeRequested`; a media primitive may emit
 * `longPressed`. The BLUEPRINT wires either signal to `host.itemOptions`
 * (Save when the payload has downloadable media, then a destructive Delete).
 * `host.crudRemove` is the delete PRIMITIVE that sheet's Delete tile calls —
 * it is not a long-press target.
 */
export function longPressHandlers(onLongPress) {
  if (!onLongPress) return {};
  let timer = null;
  const clear = () => { if (timer) { clearTimeout(timer); timer = null; } };
  return {
    onContextMenu: (e) => { e.preventDefault(); onLongPress(e); },
    onPointerDown: () => { clear(); timer = setTimeout(() => { timer = null; onLongPress(); }, 500); },
    onPointerUp: clear,
    onPointerLeave: clear,
    onPointerCancel: clear,
  };
}

/**
 * A selection component's ACTIVE key — the tile that is lit, the chip that is
 * on. Local state by default: owning its own selection is what makes a chip bar
 * droppable into any blueprint.
 *
 * That local state dies with the component, and a page change unmounts it while
 * the filter it applied lives on in the grid's slot inbox — you come back to a
 * filtered grid whose tile no longer reads as active. So a host that outlives
 * the page may hand the runtime a store (`interaction.ui`, see slots.js) and
 * this reads from THAT instead, keyed by node id. Absent one — the app, and any
 * standalone render — this is exactly the old `useState`.
 */
export function useSelection(nodeId, initial = null) {
  const rt = useViewIR();
  const [local, setLocal] = useState(initial);
  const store = rt?.ui || null;
  const setUi = rt?.setUi;
  const shared = store ? store[nodeId] : undefined;
  const setActive = useCallback(
    (next) => (setUi ? setUi(nodeId, next) : setLocal(next)),
    [setUi, nodeId],
  );
  return [store ? (shared === undefined ? initial : shared) : local, setActive];
}

/**
 * Format a number for display; null/undefined → an em dash, never "0".
 * Delegates to the kit's single formatter so a component and a derive can
 * never disagree about how the same value reads.
 */
export function fmt(v, unit = '') {
  const s = formatNumber(v);
  return unit ? `${s}${unit}` : s;
}

/** `field_name` → `Field name`. A key is a wire name; a label is English. */
export function fieldLabel(key) {
  const s = String(key ?? '').replace(/_/g, ' ').trim();
  return s ? `${s[0].toUpperCase()}${s.slice(1)}` : '';
}

/**
 * `labels: {field: {value: "Label"}}` — the ENUM → DISPLAY table, and the one
 * thing a detail table could not say.
 *
 * A row carries wire values: `supplement`, `both`, `b`, `avoid`. Those are the
 * right thing to STORE and the wrong thing to show — "Kind: supplement" and
 * "Audience: both" are the model's vocabulary printed at the user, and the
 * blueprint had no way to translate them short of a derive per enum. The table
 * is declared beside the fields it decodes, so a value with no entry falls
 * through to itself rather than disappearing.
 */
export function labelFor(labels, field, value) {
  const table = labels && typeof labels === 'object' ? labels[field] : null;
  if (!table || typeof table !== 'object') return null;
  const hit = table[String(value)];
  return hit == null || hit === '' ? null : String(hit);
}

function kvScalar(value, labels, field) {
  const mapped = labelFor(labels, field, value);
  if (mapped) return mapped;
  if (typeof value === 'number') return fmt(value);
  return String(value);
}

/**
 * ONE kv cell → the LINES it draws. The reason it returns lines rather than a
 * string is the OBJECT field.
 *
 * A model-authored row grows structured fields (`safety: {verdict, reason}`),
 * and both kv renderers used to end at `JSON.stringify(v)` — so a nutrition
 * page printed `{"verdict":"caution","reason":"…"}` on screen, as a fact about
 * the user's lunch. An object is not one value; it is a small table, and the
 * honest render of a small table is its rows. `exclude` drops the sub-keys the
 * page already shows elsewhere.
 *
 * Empty sub-values are dropped by the same rule the outer table obeys: a field
 * that exists and is unknown says nothing, and silence is the honest render.
 */
export function kvLines(value, { labels = null, field = null, exclude = null } = {}) {
  if (value == null || value === '' || typeof value === 'function') return [];
  const skip = new Set(exclude || []);

  if (Array.isArray(value)) {
    const parts = value
      .map((x) => (x && typeof x === 'object' ? (x.text ?? x.label ?? x.value ?? '') : x))
      .filter((x) => x != null && x !== '')
      .map((x) => kvScalar(x, labels, field));
    return parts.length ? [{ label: null, text: parts.join(', ') }] : [];
  }

  if (typeof value === 'object') {
    const out = [];
    for (const [k, v] of Object.entries(value)) {
      if (skip.has(k) || v == null || v === '' || typeof v === 'function') continue;
      if (Array.isArray(v) && !v.length) continue;
      const text = Array.isArray(v) || (v && typeof v === 'object')
        ? kvLines(v, { labels, field: k }).map((l) => l.text).join(' · ')
        : kvScalar(v, labels, k) || kvScalar(v, labels, field);
      if (text) out.push({ label: fieldLabel(k), text });
    }
    return out;
  }

  const text = kvScalar(value, labels, field);
  return text ? [{ label: null, text }] : [];
}
